/**
 * ResourceForm component
 * Form for creating or editing a story resource
 * Moved from ResourceEditorPage/components to molecules for reuse
 */

import * as React from 'react';
import { createPortal } from 'react-dom';
import { marked } from 'marked';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Select } from '@/components/atoms/Select';
import { RichTextEditor } from '@/components/atoms/RichTextEditor';
import { cn } from '@/utils/cn';
import type { StoryResource, StoryType } from '@/types/backoffice.types';
import styles from './ResourceForm.module.scss';

export interface ResourceFormData extends Partial<StoryResource> {
  type: 'text' | 'audio' | 'video';
  title: string;
  order: number;
}

export interface ResourceFormRef {
  submit: () => void;
}

interface ResourceFormProps {
  mode: 'create' | 'edit';
  resource?: StoryResource;
  storyType: StoryType;
  nextOrder: number;
  onSave: (data: ResourceFormData) => Promise<void>;
  onCancel: () => void;
  /** When 'editor', uses toolbar layout: no built-in actions, sidebar for metadata */
  layout?: 'default' | 'editor';
  /** DOM element to render sidebar content (Tipo, Título, Orden) into */
  sidebarPortalTarget?: HTMLElement | null;
}

const RESOURCE_TYPE_OPTIONS = [
  { label: 'Texto', value: 'text' },
  { label: 'Audio', value: 'audio' },
  { label: 'Vídeo', value: 'video' },
];

const LISTENING_RESOURCE_TYPE_OPTIONS = [{ label: 'Audio', value: 'audio' }];

export const ResourceForm = React.forwardRef<ResourceFormRef, ResourceFormProps>(function ResourceForm(
  {
    mode,
    resource,
    storyType,
    nextOrder,
    onSave,
    onCancel,
    layout = 'default',
    sidebarPortalTarget,
  },
  ref
) {
  const isListening = storyType === 'listening';
  const typeOptions = isListening ? LISTENING_RESOURCE_TYPE_OPTIONS : RESOURCE_TYPE_OPTIONS;

  const [type, setType] = React.useState<'text' | 'audio' | 'video'>(
    resource?.type ?? (isListening ? 'audio' : 'text')
  );
  const [title, setTitle] = React.useState(resource?.title ?? '');
  const [order, setOrder] = React.useState(resource?.order ?? nextOrder);
  const [content, setContent] = React.useState(() => {
    const raw = resource?.content ?? '';
    // TipTap needs HTML — convert plain text newlines to <p> tags
    if (raw && !/<[a-z]/i.test(raw)) {
      return raw
        .split(/\n\n+/)
        .map((para) => `<p>${para.replace(/\n/g, '<br/>')}</p>`)
        .filter((p) => p !== '<p></p>')
        .join('') || raw;
    }
    return raw;
  });
  const [url, setUrl] = React.useState(resource?.url ?? '');
  const [duration, setDuration] = React.useState<number>(resource?.duration ?? 0);
  const [transcript, setTranscript] = React.useState(resource?.transcript ?? '');
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const mdFileInputRef = React.useRef<HTMLInputElement>(null);

  const handleMarkdownImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const markdown = event.target?.result as string;
      // breaks:true → single newlines become <br> within the same <p> (semantically correct)
      // Double blank lines in the .md create new <p> blocks as expected
      const html = marked.parse(markdown, { breaks: true }) as string;
      setContent(html);
    };
    reader.readAsText(file, 'utf-8');
    e.target.value = '';
  };

  const handleTypeChange = (value: string) => {
    setType(value as 'text' | 'audio' | 'video');
    setContent('');
    setUrl('');
    setDuration(0);
    setTranscript('');
    setErrors({});
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) newErrors.title = 'El título es obligatorio';

    if (type === 'text' && (!content.trim() || content === '<p></p>')) {
      newErrors.content = 'El contenido es obligatorio para recursos de texto';
    }

    if ((type === 'audio' || type === 'video') && !url.trim()) {
      newErrors.url = 'La URL es obligatoria';
    }

    if (type === 'audio' && isListening && !transcript.trim()) {
      newErrors.transcript = 'El transcript es obligatorio para historias de escucha';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const formData: ResourceFormData = {
        ...(resource?.id ? { id: resource.id } : {}),
        type,
        title: title.trim(),
        order,
        ...(type === 'text' ? { content: content.trim() } : {}),
        ...((type === 'audio' || type === 'video') ? { url: url.trim(), duration } : {}),
        ...(transcript.trim() ? { transcript: transcript.trim() } : {}),
      };
      await onSave(formData);
    } catch (error) {
      console.error('Error saving resource:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  React.useImperativeHandle(ref, () => ({
    submit: handleSubmit,
  }));

  const isEditorLayout = layout === 'editor';

  const sidebarContent = (
    <div className={styles.resourceForm__sidebar}>
      <div className={styles.resourceForm__group}>
        <label htmlFor="resource-type-sidebar">Tipo</label>
        <Select
          value={type}
          onValueChange={handleTypeChange}
          options={typeOptions}
          disabled={mode === 'edit' || isListening}
        />
        {isListening && (
          <p className={styles.resourceForm__hint}>
            Las historias de escucha solo admiten recursos de audio.
          </p>
        )}
      </div>
      <div className={styles.resourceForm__group}>
        <label htmlFor="resource-title-sidebar">Título</label>
        <Input
          id="resource-title-sidebar"
          type="text"
          placeholder="Título del recurso"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        {errors.title && <p className={styles.resourceForm__error}>{errors.title}</p>}
      </div>
      <div className={styles.resourceForm__group}>
        <label htmlFor="resource-order-sidebar">Orden</label>
        <Input
          id="resource-order-sidebar"
          type="number"
          value={String(order)}
          onChange={(e) => setOrder(Number(e.target.value))}
        />
      </div>
    </div>
  );

  return (
    <div className={cn(styles.resourceForm, isEditorLayout && styles['resourceForm--editor'])}>
      {sidebarPortalTarget && createPortal(sidebarContent, sidebarPortalTarget, 'resource-form-sidebar')}

      {/* Tipo, Título y Orden: en main cuando layout default, en sidebar cuando editor */}
      {!isEditorLayout && (
        <>
          <div className={styles.resourceForm__group}>
            <label htmlFor="resource-type">Tipo</label>
            <Select
              value={type}
              onValueChange={handleTypeChange}
              options={typeOptions}
              disabled={mode === 'edit' || isListening}
            />
            {isListening && (
              <p className={styles.resourceForm__hint}>
                Las historias de escucha solo admiten recursos de audio.
              </p>
            )}
          </div>
          <div className={styles.resourceForm__group}>
            <label htmlFor="resource-title">Título</label>
            <Input
              id="resource-title"
              name="resource-title"
              type="text"
              placeholder="Título del recurso"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            {errors.title && <p className={styles.resourceForm__error}>{errors.title}</p>}
          </div>
          <div className={styles.resourceForm__group}>
            <label htmlFor="resource-order">Orden</label>
            <Input
              id="resource-order"
              name="resource-order"
              type="number"
              value={String(order)}
              onChange={(e) => setOrder(Number(e.target.value))}
            />
          </div>
        </>
      )}

      {/* Text Content */}
      {type === 'text' && (
        <div className={cn(styles.resourceForm__group, isEditorLayout && styles['resourceForm__group--fill'])}>
          <div className={styles.resourceForm__importRow}>
            <input
              ref={mdFileInputRef}
              type="file"
              accept=".md"
              className={styles.resourceForm__mdInput}
              onChange={handleMarkdownImport}
            />
            <Button
              variant="secondary"
              type="button"
              size="sm"
              onClick={() => mdFileInputRef.current?.click()}
            >
              Importar .md
            </Button>
          </div>
          <div className={isEditorLayout ? styles.resourceForm__editorContent : undefined}>
            <RichTextEditor
              value={content}
              onChange={setContent}
              placeholder="Contenido del recurso"
              variant={isEditorLayout ? 'plain' : 'default'}
              fill={isEditorLayout}
            />
          </div>
          {errors.content && <p className={styles.resourceForm__error}>{errors.content}</p>}
        </div>
      )}

      {/* Audio/Video URL */}
      {(type === 'audio' || type === 'video') && (
        <>
          <div className={styles.resourceForm__group}>
            <label htmlFor="resource-url">
              {type === 'audio' ? 'Audio URL' : 'Video URL'}
            </label>
            <Input
              id="resource-url"
              name="resource-url"
              type="url"
              placeholder={type === 'audio' ? 'https://example.com/audio.mp3' : 'https://example.com/video.mp4'}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            {errors.url && <p className={styles.resourceForm__error}>{errors.url}</p>}
          </div>

          <div className={styles.resourceForm__group}>
            <label htmlFor="resource-duration">Duración (segundos)</label>
            <Input
              id="resource-duration"
              name="resource-duration"
              type="number"
              placeholder="Duración en segundos"
              value={String(duration)}
              onChange={(e) => setDuration(Number(e.target.value))}
            />
          </div>
        </>
      )}

      {/* Transcript URL */}
      {(type === 'audio' || type === 'video') && (
        <div className={styles.resourceForm__group}>
          <label htmlFor="resource-transcript">
            URL Transcript (JSON)
            {type === 'audio' && isListening && (
              <span className={styles.resourceForm__required} aria-label="obligatorio"> *</span>
            )}
          </label>
          <Input
            id="resource-transcript"
            name="resource-transcript"
            type="url"
            placeholder="https://vps.example.com/transcripts/audio-name.json"
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            aria-required={type === 'audio' && isListening}
          />
          {errors.transcript && (
            <p className={styles.resourceForm__error}>{errors.transcript}</p>
          )}
        </div>
      )}

      {/* Actions - hidden when editor layout (toolbar has Save) */}
      {!isEditorLayout && (
        <div className={styles.resourceForm__actions}>
          <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Guardando...' : mode === 'create' ? 'Añadir recurso' : 'Guardar cambios'}
          </Button>
        </div>
      )}
    </div>
  );
});
